/**
 * Cross-platform country flag.
 *
 * Why a component, not a Unicode emoji: macOS / iOS / Android render
 * the regional-indicator characters (🇬🇧 🇺🇸) as flags natively, but
 * Windows 10/11 with the default Segoe UI Emoji font does NOT , the
 * user sees plain letter pairs like "GB" or "US" instead. So we use
 * the `flag-icons` CSS sprite library (loaded in index.html) which
 * works identically on every browser/OS combination.
 *
 * Usage:
 *   <Flag code="GB" />
 *   <Flag code="US" size="lg" rounded />
 *   <Flag code={null} fallback="🌍" />
 */
export default function Flag({
  code,
  size = "md", // sm | md | lg
  rounded = true,
  className = "",
  title,
  fallback = null,
}) {
  if (!code || typeof code !== "string" || code.length !== 2) {
    return fallback ? <span className={className}>{fallback}</span> : null;
  }

  // flag-icons uses ISO-2 lower-case class names: fi-gb, fi-us, fi-fr…
  const cls = `fi fi-${code.toLowerCase()}`;
  // Vertically aligned tile so it sits cleanly inline with text. The
  // library defaults to a 4:3 aspect ratio; we lock the width and let
  // it compute height (h-auto isn't reliable across browsers).
  const dims = {
    sm: "w-4 h-3",       // 16x12
    md: "w-[18px] h-[13px]", // ~18x13
    lg: "w-6 h-[18px]",  // 24x18
  }[size];
  const radius = rounded ? "rounded-[2px]" : "";

  return (
    <span
      className={`${cls} ${dims} ${radius} inline-block align-[-2px] flex-shrink-0 ${className}`}
      role="img"
      aria-label={title || `${code.toUpperCase()} flag`}
      title={title}
    />
  );
}
